import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  IconButton,
  Badge,
  Popover,
  Typography,
  List,
  ListItem,
  ListItemText,
  Divider,
  Tooltip,
  Chip
} from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import { apiFetch } from '../../api/client';
import CalendarioDialog from '../calendario/CalendarioDialog';
import useContratosAtividadesPendentes from '../../hooks/useContratosAtividadesPendentes';

/**
 * Barra superior do módulo Contratos: calendário, sino (atividades PENDENTES) e Análise Comparativa.
 */
export default function ContratosModuloHeader({ sx }) {
  const navigate = useNavigate();
  const { pendentes, total, recarregar } = useContratosAtividadesPendentes();
  const [notifAnchor, setNotifAnchor] = useState(null);
  const [calOpen, setCalOpen] = useState(false);

  const abrirAtividade = (atividade) => {
    setNotifAnchor(null);
    navigate('/contratos/atividades', {
      state: { highlightProcessoId: atividade.processoId || null, highlightAtividadeId: atividade.id }
    });
  };

  const fetchEventos = async (params) => {
    const q = new URLSearchParams();
    Object.entries(params || {}).forEach(([k, v]) => {
      if (v != null && v !== '') q.set(k, String(v));
    });
    const res = await apiFetch(`/api/calendario/eventos?${q.toString()}`);
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Erro ao carregar calendário');
    }
    return res.json();
  };

  const fetchUsuarios = async () => {
    const res = await apiFetch('/api/calendario/usuarios-destino');
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || 'Erro ao carregar usuários');
    return data;
  };

  const fetchPrestadores = async () => {
    const res = await apiFetch('/api/calendario/prestadores-destino');
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || 'Erro ao carregar prestadores');
    return data;
  };

  const criarEvento = useCallback(async (payload) => {
    const res = await apiFetch('/api/calendario/eventos', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || 'Erro ao criar evento');
    recarregar();
  }, [recarregar]);

  const atualizarEvento = useCallback(async (id, payload) => {
    const res = await apiFetch(`/api/calendario/eventos/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || 'Erro ao atualizar evento');
    recarregar();
  }, [recarregar]);

  const excluirEvento = useCallback(async (id) => {
    const res = await apiFetch(`/api/calendario/eventos/${id}`, { method: 'DELETE' });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || 'Erro ao excluir evento');
    recarregar();
  }, [recarregar]);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: 1,
        flexWrap: 'wrap',
        ...sx
      }}
    >
      <Button
        variant="outlined"
        size="small"
        startIcon={<CompareArrowsIcon />}
        onClick={() => navigate('/contratos/analise-comparativa')}
        sx={{
          textTransform: 'none',
          borderRadius: 2,
          borderColor: 'rgba(25,118,210,0.35)',
          color: 'primary.main',
          fontWeight: 600
        }}
      >
        Análise Comparativa
      </Button>

      <Tooltip title="Calendário / agenda">
        <IconButton
          onClick={() => setCalOpen(true)}
          sx={{ color: 'primary.main', backgroundColor: 'rgba(25,118,210,0.08)' }}
          aria-label="Calendário"
        >
          <CalendarTodayIcon />
        </IconButton>
      </Tooltip>

      <Tooltip title="Atividades pendentes de contratos">
        <IconButton
          onClick={(e) => setNotifAnchor(e.currentTarget)}
          sx={{ color: 'primary.main', backgroundColor: 'rgba(239,108,0,0.1)' }}
          aria-label="Alertas de atividades"
        >
          <Badge badgeContent={total} color="error" max={99}>
            <NotificationsNoneIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={Boolean(notifAnchor)}
        anchorEl={notifAnchor}
        onClose={() => setNotifAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ p: 1, minWidth: 320, maxWidth: 400 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.5 }}>
            <PendingActionsIcon sx={{ color: '#ef6c00', fontSize: 20 }} />
            <Typography variant="subtitle1" fontWeight={700}>
              Atividades pendentes
            </Typography>
            {total > 0 && (
              <Chip size="small" label={total} sx={{ ml: 'auto', bgcolor: 'rgba(239,108,0,0.12)', color: '#ef6c00' }} />
            )}
          </Box>
          <Divider />
          <List dense sx={{ maxHeight: 360, overflow: 'auto' }}>
            {pendentes.length === 0 ? (
              <ListItem>
                <ListItemText primary="Nenhuma atividade pendente." secondary="Você está em dia no módulo de contratos." />
              </ListItem>
            ) : (
              pendentes.map((a) => (
                <ListItem
                  key={a.id}
                  sx={{ bgcolor: 'rgba(239,108,0,0.06)', cursor: 'pointer', mb: 0.5, borderRadius: 1 }}
                  onClick={() => abrirAtividade(a)}
                >
                  <ListItemText
                    primary={a.titulo || a.solicitacaoTitulo || 'Atividade pendente'}
                    secondary={
                      <>
                        {[a.processoCodigo, a.destinoLabel || a.destino].filter(Boolean).join(' · ')}
                        {a.criadoEm && (
                          <Typography component="span" variant="caption" display="block" color="text.secondary">
                            {new Date(a.criadoEm).toLocaleString('pt-BR')}
                          </Typography>
                        )}
                      </>
                    }
                    primaryTypographyProps={{ fontWeight: 700, fontSize: '0.9rem' }}
                  />
                </ListItem>
              ))
            )}
          </List>
          <Button
            size="small"
            fullWidth
            onClick={() => {
              setNotifAnchor(null);
              navigate('/contratos/atividades');
            }}
            sx={{ mt: 1, textTransform: 'none' }}
          >
            Ver todas as atividades
          </Button>
        </Box>
      </Popover>

      <CalendarioDialog
        open={calOpen}
        onClose={() => setCalOpen(false)}
        fetchEventos={fetchEventos}
        fetchUsuarios={fetchUsuarios}
        fetchPrestadores={fetchPrestadores}
        criarEvento={criarEvento}
        atualizarEvento={atualizarEvento}
        excluirEvento={excluirEvento}
      />
    </Box>
  );
}
